import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ExternalLink } from "lucide-react";

/**
 * <PromoBanners placement="home" /> — admin-controlled affiliate banner rail.
 *
 * Why a separate rail and not interleaved cards? UX preservation. Users
 * scrolling the marketplace expect uninterrupted listing tiles. We carve out
 * ONE dedicated horizontal strip per page (between content sections), labelled
 * "Sponsored partner" so there's no ambiguity, and never inject ads mid-grid.
 *
 * Each banner is a single thin 4:1 (or wider) image with title + tiny CTA.
 * Clicks fire a beacon to `/api/promo-banners/:id/click` for ROI tracking;
 * impressions are bumped server-side when the list endpoint serves them.
 * Renders nothing when no banners exist — zero footprint when empty.
 */
export default function PromoBanners({ placement = "home", limit = 2, className = "" }) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        let alive = true;
        api.get(`/promo-banners?placement=${encodeURIComponent(placement)}&limit=${limit}`)
            .then(({ data }) => { if (alive) setItems(data?.items || []); })
            .catch(() => { });
        return () => { alive = false; };
    }, [placement, limit]);

    if (items.length === 0) return null;

    const onClick = (id) => {
        // Beacon — fails silently if the user is offline. We use sendBeacon
        // when available so navigation doesn't cancel the request.
        try {
            const url = `${api.defaults.baseURL}/promo-banners/${id}/click`;
            if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([], { type: "application/json" }));
            else fetch(url, { method: "POST", keepalive: true }).catch(() => { });
        } catch { /* ignore */ }
    };

    return (
        <section data-testid={`promo-banners-${placement}`} className={`my-3 ${className}`}>
            <div className="flex items-center gap-2 mb-1.5 px-0.5">
                <span className="text-[10px] uppercase tracking-widest font-extrabold text-aduku-navy/55">Sponsored partner</span>
                <span className="text-[10px] text-aduku-navy/35">·</span>
                <span className="text-[10px] text-aduku-navy/45">supports Adukoo</span>
            </div>
            <div className="space-y-2">
                {items.map((b) => (
                    <a
                        key={b.id}
                        href={b.target_url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => onClick(b.id)}
                        data-testid={`promo-banner-${b.id}`}
                        className="group block bg-white border border-aduku-border hover:border-aduku-yellow rounded-xl overflow-hidden transition shadow-sm hover:shadow-md"
                    >
                        <div className="flex items-center">
                            <div className="w-24 sm:w-40 aspect-[4/1] shrink-0 bg-aduku-navy/5 overflow-hidden">
                                <img
                                    src={b.image_url}
                                    alt={b.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    referrerPolicy="no-referrer"
                                />
                            </div>
                            <div className="flex-1 px-3 sm:px-4 py-2.5 min-w-0">
                                <div className="font-display font-black text-aduku-navy text-sm sm:text-base line-clamp-1">{b.title}</div>
                                {b.description && (
                                    <div className="text-aduku-navy/65 text-xs line-clamp-1 mt-0.5">{b.description}</div>
                                )}
                            </div>
                            <div className="hidden sm:flex items-center gap-1 px-3 text-aduku-navy/55 text-xs font-bold shrink-0 group-hover:text-aduku-yellow">
                                Open <ExternalLink className="w-3 h-3" />
                            </div>
                        </div>
                    </a>
                ))}
            </div>
        </section>
    );
}
